import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  MapPin,
  ArrowRight,
  ChevronDown,
  Route as RouteIcon,
  Calendar,
  Plus,
  Pencil,
  Trash2,
  X,
  Store as StoreIcon,
} from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  scheduled_date: "",
  shops: [],
  notes: "",
};
const emptyShopDraft = {
  name: "",
  address: "",
  store_type: "retail",
  owner_name: "",
  phone: "",
  lat: "",
  lng: "",
};

export default function SalesmanHome() {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [allStores, setAllStores] = useState([]);
  const [ordersToday, setOrdersToday] = useState(0);
  const [target, setTarget] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [shopDraft, setShopDraft] = useState(emptyShopDraft);

  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const [r, o, s, t] = await Promise.all([
      api.get("/routes/my"),
      api.get("/orders"),
      api.get("/stores"),
      api.get("/targets/me").catch(() => ({ data: null })),
    ]);
    setRoutes(r.data);
    setAllStores(s.data);
    setOrdersToday(o.data.filter((x) => x.created_at.slice(0, 10) === today).length);
    setTarget(t.data || null);
    const todays = r.data.find((x) => !x.scheduled_date || x.scheduled_date === today);
    if (todays && openId === null) setOpenId(todays.id);
  };

  useEffect(() => {
    load();
     
  }, []);

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  const dayLabel = (d) => {
    if (!d) return "Unscheduled";
    if (d === today) return "Today";
    const diff = Math.round((new Date(d) - new Date(today)) / 86400000);
    if (diff === 1) return "Tomorrow";
    if (diff > 1 && diff < 7) return `In ${diff} days`;
    if (diff < 0) return "Past";
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  const todaysCount = routes.filter(
    (r) => !r.scheduled_date || r.scheduled_date === today,
  ).length;

  const openNew = () => {
    setForm({ ...emptyForm, scheduled_date: today });
    setEditingId(null);
    setShopDraft(emptyShopDraft);
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    const existingShops = (r.stores || []).map((s) => ({
      id: s.id, // existing
      name: s.name,
      address: s.address || "",
      store_type: s.store_type || "retail",
      owner_name: s.owner_name || "",
      phone: s.phone || "",
      lat: s.lat ?? "",
      lng: s.lng ?? "",
      isExisting: true,
    }));
    setForm({
      name: r.name,
      scheduled_date: r.scheduled_date || today,
      shops: existingShops,
      notes: r.notes || "",
    });
    setEditingId(r.id);
    setShopDraft(emptyShopDraft);
    setDialogOpen(true);
  };

  const useMyLocationForDraft = () => {
    if (!navigator.geolocation) return toast.error("Location not available");
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setShopDraft((d) => ({
          ...d,
          lat: pos.coords.latitude.toFixed(6),
          lng: pos.coords.longitude.toFixed(6),
        })),
      () => toast.error("Failed to get location"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const addShopToBeat = () => {
    if (!shopDraft.name.trim()) return toast.error("Shop name required");
    if (!shopDraft.address.trim()) return toast.error("Address required");
    setForm((f) => ({
      ...f,
      shops: [
        ...f.shops,
        {
          ...shopDraft,
          name: shopDraft.name.trim(),
          address: shopDraft.address.trim(),
          tempId: `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        },
      ],
    }));
    setShopDraft(emptyShopDraft);
    toast.success("Shop added to beat");
  };

  const removeShopFromBeat = (idx) => {
    setForm((f) => ({ ...f, shops: f.shops.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give the beat a name");
    setSaving(true);
    try {
      // Create new shops first; collect store_ids
      const store_ids = [];
      for (const sh of form.shops) {
        if (sh.id) {
          store_ids.push(sh.id);
          continue;
        }
        const payload = {
          name: sh.name,
          address: sh.address,
          store_type: sh.store_type,
          owner_name: sh.owner_name || null,
          phone: sh.phone || null,
          lat: sh.lat === "" || sh.lat == null ? null : Number(sh.lat),
          lng: sh.lng === "" || sh.lng == null ? null : Number(sh.lng),
        };
        const { data } = await api.post("/stores", payload);
        store_ids.push(data.id);
      }
      const beatPayload = {
        name: form.name.trim(),
        scheduled_date: form.scheduled_date || null,
        store_ids,
        notes: form.notes || null,
      };
      if (editingId) {
        await api.put(`/routes/${editingId}`, beatPayload);
        toast.success("Beat updated");
      } else {
        await api.post("/routes", beatPayload);
        toast.success("Beat created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rid, e) => {
    e?.stopPropagation?.();
    if (!window.confirm("Delete this beat?")) return;
    try {
      await api.delete(`/routes/${rid}`);
      toast.success("Beat deleted");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const startEdit = (r, e) => {
    e?.stopPropagation?.();
    openEdit(r);
  };

  const filteredStores = allStores; // unused — kept to avoid breaking other refs (none in dialog)
  void filteredStores;

  return (
    <div className="space-y-6" data-testid="salesman-home">
      <div>
        <p className="overline text-[color:var(--muted)]">Good day</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
          {user?.name?.split(" ")[0]} 👋
        </h1>
        <p className="text-[color:var(--muted)] mt-1">
          {new Date().toLocaleDateString("en-IN", {
            weekday: "long",
            day: "numeric",
            month: "short",
          })}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-[color:var(--ink)] text-white p-4">
          <div className="overline text-white/60">Today's beats</div>
          <div className="font-display text-3xl font-bold mt-2">{todaysCount}</div>
        </div>
        <div className="rounded-xl bg-[color:var(--primary)] text-white p-4">
          <div className="overline text-white/80">Orders today</div>
          <div className="font-display text-3xl font-bold mt-2">{ordersToday}</div>
        </div>
      </div>

      {target && target.target > 0 && (
        <div
          className="rounded-xl border border-[color:var(--border)] bg-white p-4 ink-shadow"
          data-testid="target-progress-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="overline text-[color:var(--muted)]">Monthly target</div>
              <div className="font-display text-2xl font-bold mt-1 tabular-nums">
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                  maximumFractionDigits: 0,
                }).format(target.achieved)}{" "}
                <span className="text-sm text-[color:var(--muted)] font-normal">
                  / {new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: "INR",
                    maximumFractionDigits: 0,
                  }).format(target.target)}
                </span>
              </div>
            </div>
            <div
              className={`px-3 py-1 rounded-full text-sm font-semibold tabular-nums ${
                target.pct >= 100
                  ? "bg-[color:var(--moss)]/15 text-[color:var(--moss)]"
                  : target.pct >= 60
                  ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                  : "bg-[color:var(--primary)]/10 text-[color:var(--primary)]"
              }`}
              data-testid="target-pct-badge"
            >
              {target.pct}%
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                target.pct >= 100
                  ? "bg-[color:var(--moss)]"
                  : "bg-[color:var(--primary)]"
              }`}
              style={{ width: `${Math.min(100, target.pct)}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-[color:var(--muted)]">
            <span>{target.days_left} days left</span>
            {target.daily_required > 0 ? (
              <span>
                Need{" "}
                <b className="text-[color:var(--ink)] tabular-nums">
                  {new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: "INR",
                    maximumFractionDigits: 0,
                  }).format(target.daily_required)}
                </b>{" "}
                / day
              </span>
            ) : target.pct >= 100 ? (
              <span className="text-[color:var(--moss)] font-semibold">
                Target hit 🎉
              </span>
            ) : null}
          </div>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-bold">My beats</h2>
          <Button
            onClick={openNew}
            className="h-10 rounded-xl bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white btn-tap"
            data-testid="salesman-create-beat-button"
          >
            <Plus className="h-4 w-4 mr-1" /> New beat
          </Button>
        </div>

        {routes.length === 0 && (
          <div className="rounded-xl border border-dashed border-[color:var(--border)] p-8 text-center text-[color:var(--muted)]">
            No beats yet. Tap <b>New beat</b> to plan your first route.
          </div>
        )}

        <div className="space-y-3">
          {routes.map((r) => {
            const expanded = openId === r.id;
            const isToday = !r.scheduled_date || r.scheduled_date === today;
            return (
              <div
                key={r.id}
                className="rounded-xl border border-[color:var(--border)] bg-white ink-shadow overflow-hidden"
                data-testid={`beat-card-${r.id}`}
              >
                <div className="flex items-stretch">
                  <button
                    type="button"
                    onClick={() => toggle(r.id)}
                    className="flex-1 flex items-center gap-3 p-4 text-left btn-tap hover:bg-[color:var(--surface-2)]/40"
                    data-testid={`beat-toggle-${r.id}`}
                  >
                    <div
                      className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                        isToday
                          ? "bg-[color:var(--primary)]/10 text-[color:var(--primary)]"
                          : "bg-[color:var(--surface-2)] text-[color:var(--muted)]"
                      }`}
                    >
                      <RouteIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold ${
                            isToday
                              ? "bg-[color:var(--primary)]/10 text-[color:var(--primary)]"
                              : "bg-[color:var(--surface-2)] text-[color:var(--muted)]"
                          }`}
                        >
                          {dayLabel(r.scheduled_date)}
                        </span>
                      </div>
                      <div className="font-display font-bold text-lg mt-1 truncate">
                        {r.name}
                      </div>
                      <div className="text-xs text-[color:var(--muted)] flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {r.stores?.length || 0} shops
                        </span>
                        {r.scheduled_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {r.scheduled_date}
                          </span>
                        )}
                        {r.salesman?.name && r.salesman_id !== user?.id && (
                          <span
                            className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--surface-2)] text-[color:var(--muted)] uppercase tracking-wide font-semibold"
                            data-testid={`beat-owner-${r.id}`}
                          >
                            by {r.salesman.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-[color:var(--muted)] shrink-0 transition-transform ${
                        expanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div className="flex flex-col border-l border-[color:var(--border)]">
                    <button
                      onClick={(e) => startEdit(r, e)}
                      className="flex-1 px-3 text-[color:var(--muted)] hover:text-[color:var(--ink)] border-b border-[color:var(--border)]"
                      aria-label="Edit beat"
                      data-testid={`beat-edit-${r.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => remove(r.id, e)}
                      className="flex-1 px-3 text-[color:var(--muted)] hover:text-[color:var(--primary)]"
                      aria-label="Delete beat"
                      data-testid={`beat-delete-${r.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {expanded && (
                  <ul
                    className="divide-y divide-[color:var(--border)] border-t border-[color:var(--border)]"
                    data-testid={`beat-stops-${r.id}`}
                  >
                    {(r.stores || []).length === 0 && (
                      <li className="p-4 text-sm text-[color:var(--muted)] text-center">
                        No shops in this beat yet. Tap edit to add.
                      </li>
                    )}
                    {(r.stores || []).map((s, i) => (
                      <li key={s.id}>
                        <Link
                          to={`/app/stores/${s.id}`}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-[color:var(--surface-2)]/50"
                          data-testid={`beat-stop-${s.id}`}
                        >
                          <span className="h-8 w-8 rounded-full bg-[color:var(--surface-2)] text-[color:var(--ink)] flex items-center justify-center text-sm font-semibold shrink-0">
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{s.name}</div>
                            <div className="text-xs text-[color:var(--muted)] truncate flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {s.address}
                            </div>
                          </div>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${
                              s.store_type === "wholesale"
                                ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                                : "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
                            }`}
                          >
                            {s.store_type}
                          </span>
                          <ArrowRight className="h-4 w-4 text-[color:var(--muted)] shrink-0" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Beat create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-md max-h-[90vh] overflow-y-auto"
          data-testid="beat-dialog"
        >
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit beat" : "Plan new beat"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Beat name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Monday Market Route"
                className="h-12"
                data-testid="beat-name-input"
              />
            </div>
            <div>
              <Label>Scheduled date</Label>
              <Input
                type="date"
                value={form.scheduled_date}
                onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                className="h-12"
                data-testid="beat-date-input"
              />
            </div>

            {/* Shops added to this beat */}
            <div>
              <Label>Shops in this beat ({form.shops.length})</Label>
              {form.shops.length === 0 ? (
                <div className="mt-1 rounded-lg border border-dashed border-[color:var(--border)] p-4 text-center text-xs text-[color:var(--muted)]">
                  No shops yet. Use the form below to add shops to this beat.
                </div>
              ) : (
                <ul
                  className="mt-1 rounded-lg border border-[color:var(--border)] divide-y divide-[color:var(--border)] overflow-hidden"
                  data-testid="beat-shops-list"
                >
                  {form.shops.map((s, i) => (
                    <li
                      key={s.id || s.tempId}
                      className="flex items-center gap-2 px-3 py-2 text-sm"
                      data-testid={`beat-shop-item-${i}`}
                    >
                      <span className="h-7 w-7 rounded-full bg-[color:var(--surface-2)] flex items-center justify-center text-xs font-semibold shrink-0">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate flex items-center gap-2">
                          <span className="truncate">{s.name}</span>
                          <span
                            className={`text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wide font-semibold shrink-0 ${
                              s.store_type === "wholesale"
                                ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                                : "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
                            }`}
                          >
                            {s.store_type}
                          </span>
                          {!s.id && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[color:var(--primary)]/10 text-[color:var(--primary)] uppercase tracking-wide font-semibold shrink-0">
                              new
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[color:var(--muted)] truncate">
                          {s.address}
                        </div>
                      </div>
                      <button
                        onClick={() => removeShopFromBeat(i)}
                        className="p-1.5 text-[color:var(--muted)] hover:text-[color:var(--primary)]"
                        aria-label="Remove shop"
                        data-testid={`beat-shop-remove-${i}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Add shop form */}
            <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-2)]/40 p-3 space-y-3">
              <div className="flex items-center gap-2">
                <StoreIcon className="h-4 w-4 text-[color:var(--primary)]" />
                <span className="font-display font-bold text-sm">Add a shop</span>
              </div>
              <div>
                <Label>Shop name</Label>
                <Input
                  value={shopDraft.name}
                  onChange={(e) =>
                    setShopDraft({ ...shopDraft, name: e.target.value })
                  }
                  className="h-11"
                  data-testid="beat-shop-name-input"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={shopDraft.address}
                  onChange={(e) =>
                    setShopDraft({ ...shopDraft, address: e.target.value })
                  }
                  className="h-11"
                  data-testid="beat-shop-address-input"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={shopDraft.store_type}
                  onValueChange={(v) =>
                    setShopDraft({ ...shopDraft, store_type: v })
                  }
                >
                  <SelectTrigger className="h-11" data-testid="beat-shop-type-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="wholesale">Wholesale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Owner</Label>
                  <Input
                    value={shopDraft.owner_name}
                    onChange={(e) =>
                      setShopDraft({ ...shopDraft, owner_name: e.target.value })
                    }
                    className="h-11"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={shopDraft.phone}
                    onChange={(e) =>
                      setShopDraft({ ...shopDraft, phone: e.target.value })
                    }
                    className="h-11"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Lat</Label>
                  <Input
                    value={shopDraft.lat}
                    onChange={(e) =>
                      setShopDraft({ ...shopDraft, lat: e.target.value })
                    }
                    className="h-11 font-mono text-xs"
                  />
                </div>
                <div>
                  <Label>Lng</Label>
                  <Input
                    value={shopDraft.lng}
                    onChange={(e) =>
                      setShopDraft({ ...shopDraft, lng: e.target.value })
                    }
                    className="h-11 font-mono text-xs"
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={useMyLocationForDraft}
                className="w-full h-11"
                data-testid="beat-shop-use-location"
              >
                <MapPin className="h-4 w-4 mr-2" /> Use my location
              </Button>
              <Button
                type="button"
                onClick={addShopToBeat}
                className="w-full h-11 bg-[color:var(--ink)] hover:bg-black text-white"
                data-testid="beat-shop-add-button"
              >
                <Plus className="h-4 w-4 mr-2" /> Add shop to beat
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={save}
              disabled={saving}
              className="w-full h-12 rounded-xl bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
              data-testid="beat-save-button"
            >
              {saving
                ? "Saving…"
                : editingId
                ? "Save beat"
                : form.shops.length > 0
                ? `Create beat with ${form.shops.length} shop${form.shops.length === 1 ? "" : "s"}`
                : "Create empty beat"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
